import axiosInstance from "@/utils/axiosInstance";
import type { FormApprovalConcurrenceState } from "./formAuthoringApi";

export interface RuntimeIdentityProjection {
  git_commit: string;
  worktree_dirty?: boolean | null;
  env_file: string;
  resolved_environment: string;
  process_environment?: string | null;
  process_env?: string | null;
  process_environment_effective?: string | null;
  settings_process_environment_match?: boolean;
  environment_truth_source?: string;
  active_db_name?: string | null;
  active_redis_db?: number | null;
  alembic_current_revision?: string | null;
  alembic_head_revision?: string | null;
  alembic_current_revisions?: string[];
  alembic_head_revisions?: string[];
  schema_current?: boolean | null;
  schema_check_error_type?: string | null;
  frontend_api_target?: string | null;
  authoritative_environment?: string | null;
}

export interface ObservabilityReadinessProjection {
  app_health_ready: boolean;
  request_id_ready: boolean;
  structured_logging_ready: boolean;
  database_ready?: boolean;
  database_error_type?: string | null;
  redis_ready?: boolean;
  redis_error_type?: string | null;
  migration_current_head_match?: boolean;
  migration_current?: string[];
  migration_heads?: string[];
  runtime_identity: RuntimeIdentityProjection;
  pilot_observability_ready?: boolean;
  production_observability_ready: boolean;
  missing: string[];
}

export interface OrganizationProjection {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
  status: string;
  launch_status: string;
  primary_admin?: string | null;
  recent_activity?: string | null;
}

export async function getObservabilityReadiness(): Promise<ObservabilityReadinessProjection> {
  const res = await axiosInstance.get<ObservabilityReadinessProjection>(
    "/system/observability-readiness"
  );
  return res.data;
}

export interface OrganizationCreatePayload {
  name: string;
}

export interface OrganizationCreateResult {
  id: number;
  name: string;
  allow_shared_phone: boolean;
  allow_multiple_disbursals: boolean;
}

export interface StaffUserProjection {
  id: number;
  username: string;
  role: StaffRoleValue | "super_admin";
  tenant_id: number;
  email?: string | null;
  phone?: string | null;
  is_active: boolean;
  is_verified: boolean;
  status: string;
}

export type StaffRoleValue = "admin" | "ddo" | "management" | "finance" | "pm" | "pc";

export interface StaffUserCreatePayload {
  username: string;
  password: string;
  role: StaffRoleValue;
  tenant_id: number;
  email: string;
  phone: string;
}

export interface StaffRoleUpdatePayload {
  role: StaffRoleValue;
  reason: string;
}

export interface WorkflowProvisionPayload {
  tenant_id: number;
  program_code: string;
  academic_year: string;
  template_config_id?: number;
}

export interface WorkflowProvisionResult {
  workflow_config_id: number;
  tenant_id: number;
  program_code: string;
  academic_year: string;
  version: number;
  is_active: boolean;
  created: boolean;
  status: "CREATED" | "REUSED_EXISTING" | string;
}

export interface ResolvedEvidenceRequirementProjection {
  requirement_key: string;
  accepted_methods: string[];
  accepted_document_types: string[];
  required_for_fields: string[];
  source: "governance_default" | "mixed" | string;
}

export interface EvidenceMethodProjection {
  id: number;
  method_key: string;
  label: string;
  description: string;
  requires_document: boolean;
  is_active: boolean;
}

export interface EvidenceDocumentTypeProjection {
  id: number;
  document_type_key: string;
  label: string;
  description: string;
  is_active: boolean;
}

export interface EvidenceRequirementProjection {
  id: number;
  requirement_key: string;
  label: string;
  description: string;
  category: string;
  is_active: boolean;
  accepted_methods: EvidenceMethodProjection[];
  accepted_document_types: EvidenceDocumentTypeProjection[];
}

export interface ResolvedEvidenceRequirementSetProjection {
  requested_fields: string[];
  resolved_requirements: ResolvedEvidenceRequirementProjection[];
  unresolved_fields: string[];
  available_requirements: EvidenceRequirementProjection[];
}

export type EvidenceContractStatus =
  | "DRAFT"
  | "READY_FOR_CONFIRMATION"
  | "CONFIRMED"
  | "SUPERSEDED";

export type EvidenceOverrideType =
  | "map_field_requirement"
  | "remove_field_requirement"
  | "enable_method"
  | "disable_method"
  | "add_document_type"
  | "remove_document_type";

export interface EvidenceContractOverrideProjection {
  id?: number | null;
  override_type: EvidenceOverrideType;
  field_key?: string | null;
  requirement_key?: string | null;
  method_key?: string | null;
  document_type_key?: string | null;
  enabled?: boolean | null;
  reason: string;
  created_by_user_id?: number | null;
  created_at?: string | null;
}

export interface EvidenceContractOverridePayload {
  override_type: EvidenceOverrideType;
  field_key?: string | null;
  requirement_key?: string | null;
  method_key?: string | null;
  document_type_key?: string | null;
  reason: string;
}

export interface EvidenceContractRevisionProjection {
  id?: number | null;
  revision_number?: number | null;
  status: EvidenceContractStatus;
  contract_hash: string;
  evidence_requirements: ResolvedEvidenceRequirementSetProjection;
  confirmation_blockers: string[];
  overrides: EvidenceContractOverrideProjection[];
  created_by_user_id?: number | null;
  confirmed_by_user_id?: number | null;
  confirmation_reason?: string | null;
  created_at?: string | null;
  confirmed_at?: string | null;
}

export interface EvidenceContractProjection {
  id?: number | null;
  tenant_id: number;
  program_code: string;
  academic_year: string;
  status: EvidenceContractStatus;
  current_revision: EvidenceContractRevisionProjection;
  created_by_user_id?: number | null;
  updated_by_user_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AdminLaunchReadinessProjection {
  program_code: string;
  academic_year: string;
  selected_workflow_template_id?: number | null;
  selected_review_process_name?: string | null;
  provisioned_workflow_config_id?: number | null;
  provisioned_workflow_config_version?: number | null;
  provisioned_workflow_template_id?: number | null;
  provisioned_review_process_name?: string | null;
  transition_count: number;
  active_form_version_count: number;
  activation_candidate_snapshot_id?: number | null;
  activation_candidate_form_draft_id?: number | null;
  activation_candidate_form_name?: string | null;
  activation_candidate_status?: string | null;
  activation_candidate_created_at?: string | null;
  activation_candidate_source_form_id?: number | null;
  activation_candidate_governance_review_severity?: string | null;
  activation_candidate_activation_blocked?: boolean | null;
  activation_candidate_can_publish: boolean;
  activation_candidate_can_activate: boolean;
  activation_candidate_blocker?: string | null;
  evidence_requirements_status: "not_available" | "resolved" | "unresolved";
  evidence_requirements: ResolvedEvidenceRequirementSetProjection;
  evidence_contract_publication_status: string;
  evidence_contract_id?: number | null;
  evidence_contract_revision_id?: number | null;
  evidence_contract_hash?: string | null;
  evidence_contract_field_set_hash?: string | null;
  evidence_contract_blockers: string[];
  activation_status: string;
  status: string;
  next_action: string;
  message: string;
}

export interface FormPublicationResult {
  tenant_id: number;
  approval_evidence_snapshot_id: number;
  form_draft_id: number;
  student_form_id: number;
  program_code: string;
  academic_year: string;
  name: string;
  version: string;
  created: boolean;
  idempotent_replay: boolean;
  publication_status: "published" | "already_published";
  next_required_action: "form_activation_required";
  evidence_contract_revision_id: number;
  contract_hash: string;
  published_by_user_id: number;
  governance_constraints: Record<string, unknown>;
}

export interface FormActivationResult {
  active_form_version_id: number;
  tenant_id: number;
  program_code: string;
  academic_year: string;
  form_version_id: number;
  approval_evidence_snapshot_id: number;
  activated_by_user_id: number;
  activated_at?: string | null;
  activation_idempotency_key: string;
  idempotent_replay: boolean;
  governance_constraints: Record<string, unknown>;
}

export interface FormApprovalEvidenceSnapshot {
  id: number;
  tenant_id: number;
  form_draft_id: number;
  submitted_by_user_id: number;
  submitted_by_role: string;
  status: string;
  governance_review_severity: string;
  required_review_domains: string[];
  final_approval_roles: string[];
  required_supporting_roles: string[];
  activation_blocked: boolean;
  applicant_visible: boolean;
  created_at?: string | null;
  governance_constraints: Record<string, unknown>;
}

export interface ApprovedFieldProjection {
  field_key: string;
  label: string;
  description: string;
  field_type: string;
  category: string;
  governance_domain: string;
  active: boolean;
  lifecycle_state: string;
  version: string;
  aliases: string[];
  importance: string;
  review_impact: string;
  platform_locked: boolean;
  why_needed?: string | null;
}

export interface ApprovedFieldCreatePayload {
  label: string;
  category: string;
  field_type: "text" | "number" | "currency" | "select" | "date" | "file";
  importance: "Low" | "Medium" | "High";
  review_impact: string;
  description: string;
  why_needed: string;
}

export interface ApprovedFieldUsageProgram {
  tenant_id: number;
  program_code: string;
  academic_year: string;
  form_name: string;
  active: boolean;
  applications: number;
  re_reviews_triggered: number;
}

export interface ApprovedFieldUsageProjection {
  field_key: string;
  program_count: number;
  total_applications: number;
  programs: ApprovedFieldUsageProgram[];
}

export type ReviewTemplateLifecycleState = "draft" | "active" | "retired" | string;

export interface ReviewTemplateTransitionPayload {
  from_status: string;
  to_status: string;
  action_code: string;
  stage?: string | null;
  decision_state?: string | null;
  allowed_roles: string[];
  requires_assignment?: boolean;
  requires_student_action?: boolean;
  is_terminal?: boolean;
  owner_role: string;
  override_roles?: string[];
}

export interface ReviewTemplateUsageSummaryProjection {
  organizations_using_template: number;
  programs_using_template: number;
  review_processes_derived: number;
  active_review_processes: number;
  applications_governed: number;
  historical_applications_governed: number;
  replacement_template_available: boolean;
  pending_setup_using_template: number;
  heavy_usage_warning: boolean;
  heavy_usage_message?: string | null;
}

export interface ReviewTemplateTransitionProjection
  extends Required<ReviewTemplateTransitionPayload> {
  id?: number | null;
}

export interface ReviewTemplateProjection {
  id: number;
  template_code: string;
  description?: string | null;
  version: number;
  lifecycle_state: ReviewTemplateLifecycleState;
  is_active: boolean;
  source_template_id?: number | null;
  created_at?: string | null;
  activated_at?: string | null;
  retired_at?: string | null;
  retirement_reason?: string | null;
  transitions: ReviewTemplateTransitionProjection[];
  usage: ReviewTemplateUsageSummaryProjection;
}

export interface ReviewTemplateUsageOrganizationProjection {
  tenant_id: number;
  organization: string;
  slug: string;
  status: string;
}

export interface ReviewTemplateUsageProgramProjection {
  tenant_id: number;
  program_code: string;
  academic_year: string;
  review_process_count: number;
  active_review_processes: number;
}

export interface ReviewTemplateUsageProjection {
  template: ReviewTemplateProjection;
  usage: ReviewTemplateUsageSummaryProjection;
  organizations: ReviewTemplateUsageOrganizationProjection[];
  programs: ReviewTemplateUsageProgramProjection[];
}

export interface ReviewTemplateCreatePayload {
  template_code: string;
  description?: string | null;
  transitions: ReviewTemplateTransitionPayload[];
}

export interface ReviewTemplateUpdatePayload {
  description?: string | null;
  transitions: ReviewTemplateTransitionPayload[];
}

export interface ReviewTemplateVersionPayload {
  template_code?: string | null;
  description?: string | null;
}

export interface WorkflowTemplateLibraryProjection {
  id: number;
  key: string;
  template_code: string;
  name: string;
  description: string;
  version: number;
  lifecycle_state: string;
  is_active: boolean;
  requires_interview: boolean;
  requires_mentoring: boolean;
  path: string[];
  transitions: ReviewTemplateTransitionProjection[];
}

export interface FundingPolicyOptionProjection {
  funding_type: "single_year" | "multi_year";
  name: string;
  min_years: number;
  allowed_years: number[];
  no_hard_limit: boolean;
}

export interface RenewalPolicyOptionProjection {
  renewal_rule: string;
  name: string;
}

export interface MentoringCriterionProjection {
  criterion: string;
  label: string;
}

export interface MentoringPolicyOptionsProjection {
  supported: boolean;
  completion_criteria: MentoringCriterionProjection[];
}

export interface ScholarshipStandardProjection {
  id: number;
  tenant_id: number;
  standard_code: string;
  name: string;
  description?: string | null;
  workflow_template: WorkflowTemplateLibraryProjection;
  funding_policy: {
    id: number;
    funding_type: "single_year" | "multi_year";
    number_of_years: number;
    name: string;
  };
  mentoring_policy: {
    id: number;
    mentoring_required: boolean;
    completion_criteria: string[];
    name: string;
  };
  renewal_policy: {
    id: number;
    renewal_rule: string;
    name: string;
  };
  lifecycle_state: string;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ScholarshipStandardCreatePayload {
  name: string;
  description?: string | null;
  workflow_template_id: number;
  funding_type: "single_year" | "multi_year";
  number_of_years?: number | null;
  renewal_rule: string;
  mentoring_required: boolean;
  mentoring_completion_criteria: string[];
  eligibility_config?: Record<string, unknown>;
  evidence_config?: Record<string, unknown>;
  scoring_config?: Record<string, unknown>;
  budget_config?: Record<string, unknown>;
}

export async function provisionWorkflowConfig(
  payload: WorkflowProvisionPayload
): Promise<WorkflowProvisionResult> {
  const res = await axiosInstance.post<WorkflowProvisionResult>(
    "/admin/users/workflow-configs/provision",
    payload
  );
  return res.data;
}

export async function listAdminLaunchReadiness(
  tenantId: number
): Promise<AdminLaunchReadinessProjection[]> {
  const res = await axiosInstance.get<AdminLaunchReadinessProjection[]>(
    `/admin/users/launch-readiness?tenant_id=${tenantId}`
  );
  return res.data;
}

function evidenceContractQuery(
  tenantId: number,
  programCode: string,
  academicYear: string
) {
  const params = new URLSearchParams({
    tenant_id: String(tenantId),
    program_code: programCode,
    academic_year: academicYear,
  });
  return params.toString();
}

export async function readEvidenceContract(
  tenantId: number,
  programCode: string,
  academicYear: string
): Promise<EvidenceContractProjection> {
  const res = await axiosInstance.get<EvidenceContractProjection>(
    `/admin/users/evidence-contracts?${evidenceContractQuery(
      tenantId,
      programCode,
      academicYear
    )}`
  );
  return res.data;
}

export async function confirmEvidenceContract(
  tenantId: number,
  programCode: string,
  academicYear: string,
  reason: string
): Promise<EvidenceContractProjection> {
  const res = await axiosInstance.post<EvidenceContractProjection>(
    `/admin/users/evidence-contracts/confirm?${evidenceContractQuery(
      tenantId,
      programCode,
      academicYear
    )}`,
    { reason }
  );
  return res.data;
}

export async function applyEvidenceContractOverride(
  tenantId: number,
  programCode: string,
  academicYear: string,
  payload: EvidenceContractOverridePayload
): Promise<EvidenceContractProjection> {
  const res = await axiosInstance.post<EvidenceContractProjection>(
    `/admin/users/evidence-contracts/overrides?${evidenceContractQuery(
      tenantId,
      programCode,
      academicYear
    )}`,
    payload
  );
  return res.data;
}

export async function listEvidenceContractRevisions(
  tenantId: number,
  programCode: string,
  academicYear: string
): Promise<EvidenceContractRevisionProjection[]> {
  const res = await axiosInstance.get<EvidenceContractRevisionProjection[]>(
    `/admin/users/evidence-contracts/revisions?${evidenceContractQuery(
      tenantId,
      programCode,
      academicYear
    )}`
  );
  return res.data;
}

export async function publishApprovalSnapshot(
  snapshotId: number
): Promise<FormPublicationResult> {
  const res = await axiosInstance.post<FormPublicationResult>(
    `/form-governance/approval-snapshots/${snapshotId}/publish`,
    {}
  );
  return res.data;
}

export async function refreshApprovalSnapshot(
  draftId: number
): Promise<FormApprovalEvidenceSnapshot> {
  const res = await axiosInstance.post<FormApprovalEvidenceSnapshot>(
    `/form-governance/form-drafts/${draftId}/review-snapshots`,
    {}
  );
  return res.data;
}

export async function approveApprovalSnapshot(
  snapshotId: number
): Promise<FormApprovalConcurrenceState> {
  const res = await axiosInstance.post<FormApprovalConcurrenceState>(
    `/form-governance/approval-snapshots/${snapshotId}/concurrence`,
    {
      decision: "approved",
      remarks: "Approved from Admin launch workspace.",
    }
  );
  return res.data;
}

export async function activateApprovalSnapshot(
  snapshotId: number
): Promise<FormActivationResult> {
  const activationKey = `admin-activation-${snapshotId}-${Date.now()}`;
  const res = await axiosInstance.post<FormActivationResult>(
    `/form-governance/approval-snapshots/${snapshotId}/activate`,
    { activation_idempotency_key: activationKey }
  );
  return res.data;
}

export async function listApprovedFields(): Promise<ApprovedFieldProjection[]> {
  const res = await axiosInstance.get<ApprovedFieldProjection[]>(
    "/admin/canonical-fields"
  );
  return res.data;
}

export async function createApprovedField(
  payload: ApprovedFieldCreatePayload
): Promise<ApprovedFieldProjection> {
  const res = await axiosInstance.post<ApprovedFieldProjection>(
    "/admin/canonical-fields",
    payload
  );
  return res.data;
}

export async function getApprovedFieldUsage(
  fieldKey: string
): Promise<ApprovedFieldUsageProjection> {
  const res = await axiosInstance.get<ApprovedFieldUsageProjection>(
    `/admin/canonical-fields/${fieldKey}/usage`
  );
  return res.data;
}

export async function retireApprovedField(
  fieldKey: string,
  reason: string
): Promise<ApprovedFieldProjection> {
  const res = await axiosInstance.post<ApprovedFieldProjection>(
    `/admin/canonical-fields/${fieldKey}/retire`,
    { reason }
  );
  return res.data;
}

export async function listReviewTemplates(): Promise<ReviewTemplateProjection[]> {
  const res = await axiosInstance.get<ReviewTemplateProjection[]>(
    "/admin/review-templates"
  );
  return res.data;
}

export async function createReviewTemplate(
  payload: ReviewTemplateCreatePayload
): Promise<ReviewTemplateProjection> {
  const res = await axiosInstance.post<ReviewTemplateProjection>(
    "/admin/review-templates",
    payload
  );
  return res.data;
}

export async function updateReviewTemplate(
  templateId: number,
  payload: ReviewTemplateUpdatePayload
): Promise<ReviewTemplateProjection> {
  const res = await axiosInstance.patch<ReviewTemplateProjection>(
    `/admin/review-templates/${templateId}`,
    payload
  );
  return res.data;
}

export async function activateReviewTemplate(
  templateId: number
): Promise<ReviewTemplateProjection> {
  const res = await axiosInstance.post<ReviewTemplateProjection>(
    `/admin/review-templates/${templateId}/activate`
  );
  return res.data;
}

export async function retireReviewTemplate(
  templateId: number,
  reason: string
): Promise<ReviewTemplateProjection> {
  const res = await axiosInstance.post<ReviewTemplateProjection>(
    `/admin/review-templates/${templateId}/retire`,
    { reason }
  );
  return res.data;
}

export async function versionReviewTemplate(
  templateId: number,
  payload: ReviewTemplateVersionPayload
): Promise<ReviewTemplateProjection> {
  const res = await axiosInstance.post<ReviewTemplateProjection>(
    `/admin/review-templates/${templateId}/versions`,
    payload
  );
  return res.data;
}

export async function getReviewTemplateUsage(
  templateId: number
): Promise<ReviewTemplateUsageProjection> {
  const res = await axiosInstance.get<ReviewTemplateUsageProjection>(
    `/admin/review-templates/${templateId}/usage`
  );
  return res.data;
}

export async function listScholarshipWorkflowTemplates(): Promise<
  WorkflowTemplateLibraryProjection[]
> {
  const res = await axiosInstance.get<WorkflowTemplateLibraryProjection[]>(
    "/admin/scholarship-standards/workflow-templates"
  );
  return res.data;
}

export async function listFundingPolicyOptions(): Promise<
  FundingPolicyOptionProjection[]
> {
  const res = await axiosInstance.get<FundingPolicyOptionProjection[]>(
    "/admin/scholarship-standards/funding-policies"
  );
  return res.data;
}

export async function listRenewalPolicyOptions(): Promise<
  RenewalPolicyOptionProjection[]
> {
  const res = await axiosInstance.get<RenewalPolicyOptionProjection[]>(
    "/admin/scholarship-standards/renewal-policies"
  );
  return res.data;
}

export async function listMentoringPolicyOptions(): Promise<
  MentoringPolicyOptionsProjection
> {
  const res = await axiosInstance.get<MentoringPolicyOptionsProjection>(
    "/admin/scholarship-standards/mentoring-policies"
  );
  return res.data;
}

export async function listScholarshipStandards(): Promise<
  ScholarshipStandardProjection[]
> {
  const res = await axiosInstance.get<ScholarshipStandardProjection[]>(
    "/admin/scholarship-standards"
  );
  return res.data;
}

export async function createScholarshipStandard(
  payload: ScholarshipStandardCreatePayload
): Promise<ScholarshipStandardProjection> {
  const res = await axiosInstance.post<ScholarshipStandardProjection>(
    "/admin/scholarship-standards",
    payload
  );
  return res.data;
}

export async function listOrganizations(): Promise<OrganizationProjection[]> {
  const res = await axiosInstance.get<OrganizationProjection[]>("/tenants");
  return res.data;
}

export async function createOrganization(
  payload: OrganizationCreatePayload
): Promise<OrganizationCreateResult> {
  const res = await axiosInstance.post<OrganizationCreateResult>(
    "/tenants/create",
    payload
  );
  return res.data;
}

export async function deactivateOrganization(
  tenantId: number,
  reason: string
): Promise<{ id: number; name: string; status: string }> {
  const res = await axiosInstance.post<{ id: number; name: string; status: string }>(
    `/tenants/${tenantId}/deactivate`,
    { reason }
  );
  return res.data;
}

export async function listStaffUsers(
  tenantId: number
): Promise<StaffUserProjection[]> {
  const res = await axiosInstance.get<StaffUserProjection[]>(
    `/admin/users/staff?tenant_id=${tenantId}`
  );
  return res.data;
}

export async function createStaffUser(
  payload: StaffUserCreatePayload
): Promise<StaffUserProjection> {
  const res = await axiosInstance.post<StaffUserProjection>(
    "/admin/users/",
    payload
  );
  return res.data;
}

export async function checkStaffUsernameAvailability(
  username: string
): Promise<{ available: boolean }> {
  const res = await axiosInstance.get<{ available: boolean }>(
    "/admin/users/username-availability",
    { params: { username } }
  );
  return res.data;
}

export async function requestStaffVerification(
  userId: number
): Promise<{ user_id: number; username: string; status: string }> {
  const res = await axiosInstance.post<{
    user_id: number;
    username: string;
    status: string;
  }>(`/admin/users/${userId}/request-verification`);
  return res.data;
}

export async function verifyStaffUser(
  userId: number
): Promise<{ ok: boolean; user_id: number; already_verified: boolean }> {
  const res = await axiosInstance.post<{
    ok: boolean;
    user_id: number;
    already_verified: boolean;
  }>("/users/verify", { user_id: userId });
  return res.data;
}

export async function verifyStaffUserWithOtp(
  userId: number,
  otp: string
): Promise<{ user_id: number; username: string; status: string }> {
  const res = await axiosInstance.post<{
    user_id: number;
    username: string;
    status: string;
  }>(`/admin/users/${userId}/verify`, { otp });
  return res.data;
}

export async function resetStaffAccess(
  userId: number,
  newPassword: string,
  reason: string
): Promise<{ user_id: number; username: string; status: string }> {
  const res = await axiosInstance.post<{
    user_id: number;
    username: string;
    status: string;
  }>(`/admin/users/${userId}/reset-password`, {
    new_password: newPassword,
    reason,
  });
  return res.data;
}

export async function updateStaffRole(
  userId: number,
  payload: StaffRoleUpdatePayload
): Promise<StaffUserProjection> {
  const res = await axiosInstance.patch<StaffUserProjection>(
    `/admin/users/${userId}/role`,
    payload
  );
  return res.data;
}

export async function suspendStaffUser(
  userId: number,
  reason: string
): Promise<StaffUserProjection> {
  const res = await axiosInstance.post<StaffUserProjection>(
    `/admin/users/${userId}/suspend`,
    { reason }
  );
  return res.data;
}

export async function reactivateStaffUser(
  userId: number,
  reason: string
): Promise<StaffUserProjection> {
  const res = await axiosInstance.post<StaffUserProjection>(
    `/admin/users/${userId}/reactivate`,
    { reason }
  );
  return res.data;
}
